/**
 * Export utilities for Mimiq Studio.
 * Handles single and bulk export of test cases and run reports.
 * Uses JSZip for multi-file ZIP archives.
 */

import JSZip from "jszip";
import type { TestCase, Run, RunStep, SharedFlow, SharedFlowFolder } from "../db/schema.js";
import type { IRStep } from "@mimiq/ir";
import { db, saveSharedFlow, saveSharedFlowFolder } from "../db/index.js";
import { generateRunHtml } from "./studioReportGenerator.js";

// ── Helpers ───────────────────────────────────────────────────────────────────

function irStepName(step: IRStep): string {
  if ("selector" in step) {
    const sel = (step as { selector?: { description?: string } }).selector;
    if (sel?.description && sel.description !== "element") return sel.description;
  }
  switch (step.type) {
    case "typeText":    return `"${step.text}"`;
    case "wait":        return `${step.durationMs}ms`;
    case "pressKey":    return step.key;
    case "launchApp":
    case "stopApp":     return step.packageName;
    case "openUrl":
    case "deepLink":    return step.url;
    case "print":       return step.message;
    case "checkpoint":  return step.message;
    case "setVariable": return step.name;
    case "swipe":       return `swipe ${step.from[0]},${step.from[1]}→${step.to[0]},${step.to[1]}`;
    case "screenshot":  return step.label ?? "screenshot";
    default:            return step.type;
  }
}

async function buildNameMap(testCaseId: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const tc = await db.testCases.get(testCaseId);
  if (tc) {
    for (const flow of tc.ir.flows) {
      for (const step of flow.steps) {
        map.set(step.id, irStepName(step));
      }
    }
  }
  return map;
}

function slugify(name: string): string {
  return name.replace(/[^a-z0-9]/gi, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").toLowerCase();
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function dateSuffix(): string {
  return new Date().toISOString().slice(0, 10); // "2026-06-20"
}

// ── Test case exports ─────────────────────────────────────────────────────────

/** Download a single TestCase as a JSON file. */
export function downloadTestCase(tc: TestCase): void {
  const json = JSON.stringify(tc.ir, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  downloadBlob(blob, `${slugify(tc.name)}.json`);
}

/** Download multiple TestCases as a ZIP archive. */
export async function downloadTestCasesAsZip(tests: TestCase[]): Promise<void> {
  if (tests.length === 0) return;
  if (tests.length === 1) { downloadTestCase(tests[0]!); return; }

  const zip = new JSZip();

  // Manifest
  zip.file("manifest.json", JSON.stringify({
    version: "1.0",
    tool: "Mimiq Studio",
    exportedAt: new Date().toISOString(),
    testCount: tests.length,
    tests: tests.map((tc) => ({ name: tc.name, id: tc.id, tags: tc.tags })),
  }, null, 2));

  // One JSON per test inside tests/ folder
  const folder = zip.folder("tests")!;
  const names = new Set<string>();
  for (const tc of tests) {
    let filename = `${slugify(tc.name)}.json`;
    // Avoid filename collisions
    if (names.has(filename)) filename = `${slugify(tc.name)}-${tc.id.slice(-6)}.json`;
    names.add(filename);
    folder.file(filename, JSON.stringify(tc.ir, null, 2));
  }

  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(blob, `mimiq-tests-${dateSuffix()}.zip`);
}

// ── Run report exports ────────────────────────────────────────────────────────

/** Download a single Run as a standalone HTML report. */
export async function downloadRunReport(run: Run, steps: RunStep[]): Promise<void> {
  const nameMap = run.testCaseId ? await buildNameMap(run.testCaseId) : new Map<string, string>();
  const html = await generateRunHtml(run, steps, nameMap);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const filename = `${slugify(run.testCaseName)}-${new Date(run.startedAt).toISOString().slice(0, 10)}.html`;
  downloadBlob(blob, filename);
}

/** Download multiple Runs as a ZIP of HTML reports. */
export async function downloadRunReportsAsZip(
  runs: Run[],
  stepsMap: Map<string, RunStep[]>,
): Promise<void> {
  if (runs.length === 0) return;
  if (runs.length === 1) {
    const run = runs[0]!;
    await downloadRunReport(run, stepsMap.get(run.id) ?? []);
    return;
  }

  const zip = new JSZip();
  const names = new Set<string>();

  for (const run of runs) {
    const steps = stepsMap.get(run.id) ?? [];
    const nameMap = run.testCaseId ? await buildNameMap(run.testCaseId) : new Map<string, string>();
    const html = await generateRunHtml(run, steps, nameMap);
    const dateStr = new Date(run.startedAt).toISOString().slice(0, 10);
    let filename = `${slugify(run.testCaseName)}-${dateStr}.html`;
    if (names.has(filename)) filename = `${slugify(run.testCaseName)}-${run.id.slice(-6)}.html`;
    names.add(filename);
    zip.file(filename, html);
  }

  // Summary manifest
  zip.file("_summary.json", JSON.stringify({
    version: "1.0",
    tool: "Mimiq Studio",
    exportedAt: new Date().toISOString(),
    reportCount: runs.length,
    reports: runs.map((r) => ({
      testCaseName: r.testCaseName,
      status: r.status,
      startedAt: r.startedAt,
      stepCount: r.stepCount,
      passedCount: r.passedCount,
      failedCount: r.failedCount,
    })),
  }, null, 2));

  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(blob, `mimiq-reports-${dateSuffix()}.zip`);
}

// ── SharedFlow (Function Library) export / import ─────────────────────────────

/** Build the folder path for a function (e.g. "Authentication/OAuth"). */
function fnFolderPath(folderId: string | undefined, folders: SharedFlowFolder[]): string {
  if (!folderId) return "";
  const folder = folders.find((f) => f.id === folderId);
  if (!folder) return "";
  const parent = fnFolderPath(folder.parentId, folders);
  const safe = folder.name.replace(/[/\\:*?"<>|]/g, "_");
  return parent ? `${parent}/${safe}` : safe;
}

/** Download a single SharedFlow as a .json file. */
export function downloadSharedFlow(fn: SharedFlow): void {
  const blob = new Blob([JSON.stringify(fn, null, 2)], { type: "application/json" });
  downloadBlob(blob, `${slugify(fn.name)}.mimiq-fn.json`);
}

/**
 * Download an array of SharedFlows (with their folders) as a ZIP.
 * Folder hierarchy is preserved in the ZIP structure and in manifest.json.
 */
export async function downloadSharedFlowsAsZip(
  functions: SharedFlow[],
  folders: SharedFlowFolder[],
  zipName?: string,
): Promise<void> {
  if (functions.length === 0) return;
  if (functions.length === 1 && folders.every((f) => !functions.some((fn) => fn.folderId === f.id))) {
    downloadSharedFlow(functions[0]!);
    return;
  }

  const zip = new JSZip();
  const fnFolder = zip.folder("functions")!;

  // Only include folders that are ancestors of exported functions
  const usedFolderIds = new Set<string>();
  function collectAncestors(folderId: string | undefined) {
    if (!folderId || usedFolderIds.has(folderId)) return;
    usedFolderIds.add(folderId);
    const parent = folders.find((f) => f.id === folderId)?.parentId;
    collectAncestors(parent);
  }
  functions.forEach((fn) => collectAncestors(fn.folderId));
  const exportedFolders = folders.filter((f) => usedFolderIds.has(f.id));

  // Place each function in its folder path inside the ZIP
  const usedNames = new Map<string, number>();
  for (const fn of functions) {
    const dir = fnFolderPath(fn.folderId, folders);
    const base = slugify(fn.name);
    const key = dir ? `${dir}/${base}` : base;
    const count = usedNames.get(key) ?? 0;
    usedNames.set(key, count + 1);
    const filename = count > 0 ? `${base}-${count}.mimiq-fn.json` : `${base}.mimiq-fn.json`;
    const path = dir ? `${dir}/${filename}` : filename;
    fnFolder.file(path, JSON.stringify(fn, null, 2));
  }

  // Manifest records the folder structure for accurate import
  zip.file("manifest.json", JSON.stringify({
    version:     "1.0",
    tool:        "Mimiq Studio",
    exportedAt:  new Date().toISOString(),
    type:        "function-library",
    functionCount: functions.length,
    folderCount:   exportedFolders.length,
    folders:     exportedFolders.map((f) => ({ id: f.id, name: f.name, parentId: f.parentId })),
    functions:   functions.map((fn) => ({ id: fn.id, name: fn.name, folderId: fn.folderId })),
  }, null, 2));

  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(blob, zipName ?? `mimiq-functions-${dateSuffix()}.zip`);
}

export interface ImportFunctionResult {
  imported: number;
  skipped:  number;
  errors:   string[];
}

/**
 * Import SharedFlows from a .json or .zip file.
 * Returns a summary of what was imported.
 */
export async function importSharedFlows(
  file: File,
  onProgress?: (msg: string) => void,
): Promise<ImportFunctionResult> {
  const result: ImportFunctionResult = { imported: 0, skipped: 0, errors: [] };

  if (file.name.endsWith(".json")) {
    // Single function JSON
    try {
      const text = await file.text();
      const fn = JSON.parse(text) as SharedFlow;
      if (!fn.name || !Array.isArray(fn.steps)) throw new Error("Not a valid Mimiq function file");
      await saveSharedFlow({ ...fn, folderId: undefined }, undefined); // import to root
      result.imported = 1;
    } catch (err) {
      result.errors.push(String(err));
    }
    return result;
  }

  if (!file.name.endsWith(".zip")) {
    result.errors.push("Unsupported file type — use .json or .zip");
    return result;
  }

  const zip = await JSZip.loadAsync(file);

  // Read manifest (if present) to reconstruct folder hierarchy
  let manifest: { folders?: { id: string; name: string; parentId?: string }[] } = {};
  const manifestEntry = zip.files["manifest.json"];
  if (manifestEntry) {
    try { manifest = JSON.parse(await manifestEntry.async("text")) as typeof manifest; } catch { /* ignore */ }
  }

  // Re-create folders from manifest, building a mapping from old ID → new ID
  const folderIdMap = new Map<string, string>(); // old id → new db id

  if (manifest.folders) {
    // Sort so parents come before children
    const sorted = [...manifest.folders].sort((a, b) => (a.parentId ? 1 : 0) - (b.parentId ? 1 : 0));
    for (const mf of sorted) {
      const newParentId = mf.parentId ? folderIdMap.get(mf.parentId) : undefined;
      const folder = await saveSharedFlowFolder({ name: mf.name, parentId: newParentId });
      folderIdMap.set(mf.id, folder.id);
      onProgress?.(`Created folder: ${mf.name}`);
    }
  }

  // Import each .mimiq-fn.json file
  for (const [path, entry] of Object.entries(zip.files)) {
    if (entry.dir || !path.endsWith(".mimiq-fn.json")) continue;
    try {
      const text = await entry.async("text");
      const fn = JSON.parse(text) as SharedFlow;
      if (!fn.name || !Array.isArray(fn.steps)) throw new Error(`Invalid function: ${path}`);
      // Map old folderId to new folderId
      const newFolderId = fn.folderId ? folderIdMap.get(fn.folderId) : undefined;
      await saveSharedFlow({ ...fn, folderId: newFolderId }, undefined); // always create new (don't overwrite)
      result.imported++;
      onProgress?.(`Imported: ${fn.name}`);
    } catch (err) {
      result.errors.push(`${path}: ${String(err)}`);
      result.skipped++;
    }
  }

  return result;
}

// ── ZIP import helpers ────────────────────────────────────────────────────────

export interface ZipTestEntry {
  filename: string;
  jsonText: string;
}

/**
 * Extract all .json files from a ZIP file.
 * Returns an array of { filename, jsonText } entries.
 */
export async function extractJsonsFromZip(file: File): Promise<ZipTestEntry[]> {
  const zip = await JSZip.loadAsync(file);
  const entries: ZipTestEntry[] = [];
  for (const [name, entry] of Object.entries(zip.files)) {
    if (!entry.dir && name.endsWith(".json") && !name.includes("manifest") && !name.includes("_summary")) {
      const text = await entry.async("text");
      entries.push({ filename: name.split("/").pop() ?? name, jsonText: text });
    }
  }
  return entries;
}
